import dayjs from "dayjs"
import { useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"

import { actions as documentActions } from "client/documents/actions"
import { CustomerDocument } from "client/documents/types"
import { Button } from "components/Button"
import FatButton from "components/Button/FatButton/FatButton"
import { Buttons, Card } from "components/Card/Card"
import { Carousel } from "components/Carousel/Index"
import Loader from "components/Loaders/Loader/Loader"
import Message from "components/Message/Message"
import { GenericModal } from "components/Modal/Modal"
import PreviousButton from "components/Previous/PreviousButton"
import { Important } from "components/Title/Title"
import { DocTypeToPicture } from "containers/Survey/utils"
import {
  PROFILE_DOCUMENTS_PAGE,
  PROFILE_INFORMATIONS_TAB,
  PROFILE_PAGE,
  UPLOAD_DOC_PAGE,
} from "core/constants"
import { useLanguage } from "locales"
import { Message as MessageType } from "types/redux"
import { DocumentsStore } from "types/store"

import styles from "./Profile.module.scss"

// Navigation state handed to the upload flow so it comes back to this page
export const importedDocumentsReturnState = { returnTo: PROFILE_DOCUMENTS_PAGE }

// Date shown on the cards, as on the mockup (no zero padding)
const CARD_DATE_FORMAT = "D/M/YYYY"

// Newest first. The account holder's documents and the relatives' ones are
// listed together (product decision on KIOSK-877).
export const sortDocumentsByDate = (
  documents: CustomerDocument[],
): CustomerDocument[] =>
  [...documents].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )

const DocumentCard: React.FC<{
  document: CustomerDocument
  onSelect: () => void
}> = ({ document, onSelect }) => (
  <FatButton
    onClick={onSelect}
    doNotFormat
    img={DocTypeToPicture[document.type] ?? DocTypeToPicture.other}
  >
    <div
      data-testid={`imported-document-${document.id}`}
      style={{
        fontSize: "24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <span title={document.title}>{document.title}</span>
      <span>{dayjs(document.created_at).format(CARD_DATE_FORMAT)}</span>
    </div>
  </FatButton>
)

export interface ImportedDocumentsViewProps {
  documents: CustomerDocument[]
  loading: boolean
  message?: MessageType
  previewUrl?: string
  previewLoading: boolean
  onBack: () => void
  onSelect: (document: CustomerDocument) => void
  onClosePreview: () => void
  onImport: () => void
}

// Presentational part of the page: title, 6-card carousel, "new document"
// button and the preview modal.
export const ImportedDocumentsView: React.FC<ImportedDocumentsViewProps> = ({
  documents,
  loading,
  message,
  previewUrl,
  previewLoading,
  onBack,
  onSelect,
  onClosePreview,
  onImport,
}) => {
  const { languages } = useLanguage()
  const cards = useMemo(
    () =>
      sortDocumentsByDate(documents).map((document) => (
        <DocumentCard
          key={document.id}
          document={document}
          onSelect={() => onSelect(document)}
        />
      )),
    [documents, onSelect],
  )

  return (
    <>
      <PreviousButton onClick={onBack} />
      <Card title={languages.importedDocuments}>
        <Message message={message} />
        {loading && <Loader />}
        {!loading && cards.length > 0 && <Carousel items={cards} infinite={false} />}
        {!loading && cards.length === 0 && (
          <Buttons>
            <Important text={languages.noImportedDocuments} />
          </Buttons>
        )}
        <Buttons>
          <Button wide="long" onClick={onImport}>
            {languages.uploadDocumentAddButton}
          </Button>
        </Buttons>
      </Card>
      <GenericModal
        visible={previewLoading || !!previewUrl}
        closable
        noFooter
        onClose={onClosePreview}
        style={{ width: 1400 }}
        bodyStyle={{ height: 860 }}
      >
        <div>
          {previewLoading && <Loader />}
          {previewUrl && (
            <iframe
              title="imported-document"
              className={styles.DocumentPreview}
              src={`${previewUrl}#toolbar=0`}
            />
          )}
        </div>
      </GenericModal>
    </>
  )
}

// Profile > Documents importés page: every document imported by the patient,
// with a preview on click and an entry point to the existing QR code upload flow.
export const ImportedDocuments: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    customerDocuments = [],
    customerDocumentsLoading = false,
    customerDocumentsMessage,
    previewUrl,
    previewLoading = false,
  } = useSelector(({ documents }: { documents: DocumentsStore }) => documents)

  // Fetched on every visit so a document imported meanwhile shows up
  useEffect(() => {
    dispatch(documentActions.customerDocumentsRequest())
    return () => {
      dispatch(documentActions.customerDocumentPreviewClear())
    }
  }, [dispatch])

  // The object URL is only needed while the preview is open
  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  return (
    <ImportedDocumentsView
      documents={customerDocuments}
      loading={customerDocumentsLoading}
      message={customerDocumentsMessage}
      previewUrl={previewUrl}
      previewLoading={previewLoading}
      onBack={() =>
        navigate(PROFILE_PAGE, { state: { tab: PROFILE_INFORMATIONS_TAB } })
      }
      onSelect={(document) =>
        dispatch(documentActions.customerDocumentPreviewRequest(document))
      }
      onClosePreview={() => dispatch(documentActions.customerDocumentPreviewClear())}
      onImport={() =>
        navigate(UPLOAD_DOC_PAGE, { state: importedDocumentsReturnState })
      }
    />
  )
}
